import { Injectable } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { API } from 'src/environments/environment';
import { AjaxService } from './ajax.service';
import { ObservacaoContaReceberPayload } from './fin-conta-receber-lista.service';
import { UtilService } from './util.service';

export interface SantanderConfigView {
  configurado: boolean;
  /** Nome do banco */
  descricao: string;
  codigoTransmissao: string;
  agencia: string;
  digitoAgencia: string;
  contaCorrente: string;
  digitoConta: string;
  nomeBeneficiarioCobranca: string;
  ultimoSequencial: number;
}

export interface SalvarSantanderConfigPayload {
  descricao: string;
  codigoTransmissao: string;
  agencia: string;
  digitoAgencia: string;
  contaCorrente: string;
  digitoConta: string;
  nomeBeneficiarioCobranca: string;
}

@Injectable({
  providedIn: 'root'
})
export class FinSantanderCnabService {
  constructor(
    private ajax: AjaxService,
    private util: UtilService
  ) {}

  obterConfig(): Observable<SantanderConfigView> {
    return this.ajax.get(`${API}financeiro/SantanderCnab/Config`).pipe(
      map((r: any) => {
        if (!r || (r?.hasErrors && r?.data == null)) {
          return this.vazio();
        }
        const d = this.desempacotar(r);
        const temDados =
          this.util.parseBoolean(d.configurado) ||
          Boolean(d.agencia || d.codigotransmissao || d.codigoTransmissao);
        if (!temDados) {
          return this.vazio();
        }
        return {
          configurado: true,
          descricao: String(d.descricao ?? ''),
          codigoTransmissao: String(d.codigotransmissao ?? d.codigoTransmissao ?? ''),
          agencia: String(d.agencia ?? ''),
          digitoAgencia: String(d.digitoagencia ?? d.digitoAgencia ?? ''),
          contaCorrente: String(d.contacorrente ?? d.contaCorrente ?? ''),
          digitoConta: String(d.digitoconta ?? d.digitoConta ?? ''),
          nomeBeneficiarioCobranca: String(d.nomebeneficiariocobranca ?? d.nomeBeneficiarioCobranca ?? ''),
          ultimoSequencial: Number(d.ultimosequencial ?? d.ultimoSequencial ?? 0)
        };
      })
    );
  }

  salvarConfig(payload: SalvarSantanderConfigPayload): Observable<any> {
    return this.ajax.post(`${API}financeiro/SantanderCnab/Config`, payload);
  }

  /** Gera remessa no servidor e dispara o download do arquivo (.txt, conteúdo CNAB 240). */
  gerarRemessaDownload(ids: number[]): Observable<void> {
    return this.ajax
      .postBlob(`${API}financeiro/SantanderCnab/GerarRemessaEntradaBoletos`, { ids })
      .pipe(switchMap((resp) => from(this.tratarRespostaRemessa(resp))));
  }

  /**
   * HTML de boleto demonstrativo (impressão), sem enviar CNAB.
   * Abre em nova guia; pode ser necessário permitir pop-up.
   */
  gerarBoletoDemonstrativoAbrirGuia(ids: number[], observacoes?: ObservacaoContaReceberPayload[]): Observable<void> {
    return this.ajax
      .postBlob(`${API}financeiro/SantanderCnab/GerarBoletoHtmlDemonstrativo`, { ids, observacoes: observacoes ?? [] })
      .pipe(switchMap((resp) => from(this.tratarBoletoDemonstrativo(resp))));
  }

  processarRetornoArquivo(file: File): Observable<any> {
    const fd = new FormData();
    fd.append('arquivo', file, file.name);
    return this.ajax.postFile(`${API}financeiro/SantanderCnab/ProcessarRetorno`, fd);
  }

  private vazio(): SantanderConfigView {
    return {
      configurado: false,
      descricao: '',
      codigoTransmissao: '',
      agencia: '',
      digitoAgencia: '',
      contaCorrente: '',
      digitoConta: '',
      nomeBeneficiarioCobranca: '',
      ultimoSequencial: 0
    };
  }

  private async tratarRespostaRemessa(resp: HttpResponse<Blob>): Promise<void> {
    const ct = (resp.headers.get('content-type') || '').toLowerCase();
    const blob = resp.body;
    if (!blob) {
      throw new Error('Resposta vazia do servidor.');
    }
    if (ct.includes('application/json')) {
      const texto = await this.blobParaTexto(blob);
      let j: any;
      try {
        j = JSON.parse(texto);
      } catch {
        throw new Error('Resposta inválida do servidor.');
      }
      if (j.hasErrors) {
        throw new Error(j.message || 'Não foi possível gerar a remessa.');
      }
      return;
    }

    let filename = 'remessa.txt';
    const cd = resp.headers.get('content-disposition');
    if (cd) {
      const mStar = /filename\*=UTF-8''([^;\n]+)/i.exec(cd);
      const m = /filename="?([^";\n]+)"?/i.exec(cd);
      if (mStar?.[1]) {
        try {
          filename = decodeURIComponent(mStar[1].trim());
        } catch {
          filename = mStar[1].trim();
        }
      } else if (m?.[1]) {
        filename = m[1].trim();
      }
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  private async tratarBoletoDemonstrativo(resp: HttpResponse<Blob>): Promise<void> {
    const ct = (resp.headers.get('content-type') || '').toLowerCase();
    const blob = resp.body;
    if (!blob) {
      throw new Error('Resposta vazia do servidor.');
    }
    if (ct.includes('application/json')) {
      const texto = await this.blobParaTexto(blob);
      let j: any;
      try {
        j = JSON.parse(texto);
      } catch {
        throw new Error('Resposta inválida do servidor.');
      }
      if (j.hasErrors) {
        throw new Error(j.message || 'Não foi possível gerar o boleto.');
      }
      return;
    }

    const url = URL.createObjectURL(blob);
    const janela = window.open(url, '_blank', 'noopener');
    if (!janela) {
      URL.revokeObjectURL(url);
      throw new Error('O navegador bloqueou a nova guia. Permita pop-ups para este site e tente de novo.');
    }
    setTimeout(() => URL.revokeObjectURL(url), 120000);
  }

  private blobParaTexto(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result ?? ''));
      fr.onerror = () => reject(fr.error ?? new Error('Falha ao ler resposta.'));
      fr.readAsText(blob);
    });
  }

  private desempacotar(response: any): any {
    let atual = response;
    for (let i = 0; i < 3; i++) {
      if (atual && typeof atual === 'object' && Object.prototype.hasOwnProperty.call(atual, 'data') && atual.data != null) {
        atual = atual.data;
      } else {
        break;
      }
    }
    return this.util.converterParaLowercase(atual ?? {});
  }
}
